import { Box, Card, Flex, IconButton } from "@chakra-ui/react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useMemo, useState, useEffect } from "react";
import { LoanSidebar } from "../LoanSidebar";
import Scenarios from "../Scenarios";
import LoanFieldsView from "./LoanFieldsView";
import LoanFieldsEdit from "./LoanFieldsEdit";
import { checkUiConfiguration } from "utils/uiconfighelper";
import AmplendSectionFieldsEdit from "./SectionFieldsEdit/amplend";
import SectionFieldsView from "./SectionFieldsView";
import BulletPayments from "../BulletPayments";
import { IconAlert } from "components/widgets/IconAlert";
import { LoanContext } from "providers/loan";
import { useDefaultInterest } from "../DefaultInterestTab/useDefaultInterest";
import DefaultInterestTab from "../DefaultInterestTab";
import { useFeatures } from "hooks/useFeatures";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSearchParams } from "react-router-dom";
import Signatures from "./Signatures/signatures";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import { FeeTab } from "components/elements/FeeTab/fee-tab";
import { useLoanApplicationPreferences } from "hooks/useLoanApplicationPreferences";
import { FieldValueMap } from "utils/types";
import { LoanSignature } from "@common/loans/signatures/loan-signature.model";
import { PropertyDetails } from "components/properties/components/property-details/property-details";
import { BaseRateTab } from "../BaseRateTab/base-rate-tab";

const OverviewTab = () => {
  const { user } = useTenant();
  const [isOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const { loan } = useContext(LoanContext);

  const [signatures, setSignatures] = useState<LoanSignature[]>([]);

  const [showCoBorrowerSignature, setShowCoBorrowerSignature] = useState(false);

  const { data: preferences } = useLoanApplicationPreferences();

  const { isCurrentlyDefaulted, hasDefaulted } = useDefaultInterest({
    loan_id: loan?.id || "",
  });
  const { hasFeature } = useFeatures();

  const [sideBarOpened, setSideBarOpened] = useState(true);

  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("editMode") === "true";

  useEffect(() => {
    if (editMode) {
      setEditing(true);
      setSideBarOpened(false);
    }
  }, [editMode]);

  useEffect(() => {
    if (loan) {
      const fieldValue = loan?.values.find(
        (value: any) => value?.loan_field?.identifier_key === "enable_co_borrower"
      );

      if (fieldValue) {
        setShowCoBorrowerSignature(fieldValue.value?.field?.toLowerCase() === "yes");
      }

      setSignatures(loan?.signatures || []);
    }
  }, [loan]);

  const showDefaultInterestTab = hasFeature(`default_interest_tab`);

  const defaultText = isCurrentlyDefaulted
    ? "This loan is in default"
    : "This loan was previously in default";

  const isSectionImplemented = useMemo(() => {
    return checkUiConfiguration(user, "loan_section");
  }, [user]);

  const isBulletPayment = useMemo(() => checkUiConfiguration(user, "bullet_payment"), [user]);

  const handleFieldValueChange = (values: FieldValueMap) => {
    const field = loan?.fields.find(field => field?.identifier_key === "enable_co_borrower");

    if (field) {
      const value = values[field.id];

      setShowCoBorrowerSignature(value?.toLowerCase() === "yes");
    }
  };

  const handleSignatureChange = (signatures: LoanSignature[]) => {
    setSignatures(signatures);
  };

  return (
    <Flex gap="1rem">
      <Flex direction="column" flex="1" gap={4}>
        {/* Section fields */}
        <WidgetBox>
          {hasDefaulted && <IconAlert text={defaultText} status="warning" />}

          {isSectionImplemented ? (
            editing ? (
              <AmplendSectionFieldsEdit
                columns={isOpen ? 2 : 3}
                onSave={() => setEditing(false)}
                onCancel={() => setEditing(false)}
                onChange={handleFieldValueChange}
                signatures={signatures}
              />
            ) : (
              <SectionFieldsView isOpen={isOpen} setEditing={setEditing} />
            )
          ) : editing ? (
            <LoanFieldsEdit
              columns={isOpen ? 2 : 3}
              onSave={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <LoanFieldsView isOpen={isOpen} setEditing={setEditing} />
          )}
        </WidgetBox>

        {/* Property details */}
        {loan?.property && !editing && <PropertyDetails propertyId={loan.property.id} />}

        {/* Base rates */}
        {user.type === "lender" && !editing && (
          <WidgetBox>
            <BaseRateTab hideHeader={false} />
          </WidgetBox>
        )}

        {/* Default interest */}
        {showDefaultInterestTab && !editing && (
          <WidgetBox>
            <DefaultInterestTab showTitle={true} />
          </WidgetBox>
        )}

        {/* Fees */}
        {user.type === "lender" && !editing && (
          <WidgetBox>
            <FeeTab loan={loan!} title="Fees" />
          </WidgetBox>
        )}

        {/* Bullet payments */}
        {user.type === "lender" && isBulletPayment && !editing && (
          <WidgetBox>
            <BulletPayments editing={editing} isOpen={isOpen} />
          </WidgetBox>
        )}

        {/* Scenarios */}
        {user.type === "lender" && !editing && (
          <WidgetBox>
            <Scenarios />
          </WidgetBox>
        )}

        {/* Signature Section */}
        {preferences?.enable_signatures && (
          <Signatures
            showCoBorrowerSignature={showCoBorrowerSignature}
            onSignatureChange={handleSignatureChange}
          />
        )}
      </Flex>

      <Card p="0">
        <Flex height="100%">
          <Box height="100%">
            <IconButton
              paddingTop="1.5rem"
              display="flex"
              alignItems="flex-start"
              height="100%"
              onClick={() => setSideBarOpened(!sideBarOpened)}
              aria-label="loan-sidebar"
              variant="ghost"
              borderLeftRadius="12px"
              borderRightRadius={sideBarOpened ? "0" : "12px"}
              borderRightWidth={sideBarOpened ? "1px" : "0"}
              borderRightColor="gray.100"
              _hover={{
                backgroundColor: "gray.50",
              }}
              icon={
                <FontAwesomeIcon
                  icon={["far", sideBarOpened ? "chevron-double-right" : "chevron-double-left"]}
                  color="gray.200"
                />
              }
            />
          </Box>
          <Box
            flex="1"
            display={!sideBarOpened ? "none" : "block"}
            width="350px"
            maxWidth="350px"
            minWidth="350px"
          >
            <LoanSidebar />
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

export default OverviewTab;
